import axios from 'axios';

// In production the React build is served by nginx, which proxies /api to the
// backend container — so use a relative URL. In dev, hit the API directly.
const API_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:3000/api/v1');

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  // Send/receive the httpOnly auth cookie (used by the file route). Needed so the
  // login/refresh response can set it in cross-origin dev; harmless in same-origin prod.
  // Does NOT affect the 31 <img>/<a> file-load sites — those are same-site and send
  // the cookie automatically, with no code change.
  withCredentials: true
});

// Auto-attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // HR is multi-tenant: admins/management pick an active company in the HR
  // company switcher. Attach it (scoped to /hr requests) so the backend scopes
  // every HR query to that tenant. Other modules are unaffected.
  const hrCompany = localStorage.getItem('hrCompanyId');
  const url = config.url || '';
  // /assessment shares the HR tenant context so the HR review queue follows the
  // company switcher just like the HR module does.
  if (hrCompany && (url.includes('/hr') || url.includes('/assessment'))) config.headers['X-Company-Id'] = hrCompany;
  return config;
});

// Auto-logout on 401; disambiguate 403s that are really an identity mismatch.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    // A FLAGGED LOGIN (must_change_password) is refused by the server everywhere but change-password,
    // /auth/me and logout. Say so by GOING there, not by showing every screen as broken: mark the stored
    // user flagged — the route gate in App.js reads it — and move to the change-password screen.
    if (status === 403 && error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
      try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        if (u) localStorage.setItem('user', JSON.stringify({ ...u, must_change_password: true }));
      } catch (_) { /* unreadable stored user — the redirect alone still lands them right */ }
      if (window.location.pathname !== '/change-password') window.location.href = '/change-password';
      return Promise.reject(error);
    }
    // A 403 is usually a genuine permission denial — but it can also be a stale-badge /
    // live-token split: this tab still renders user A, yet localStorage now holds user B's
    // token (a login in another tab of the same browser). That surfaces to the user as a
    // misleading "insufficient permissions" when the real problem is "wrong account."
    // Re-fetch /auth/me (the token's TRUE identity); if it differs from the rendered user,
    // rewrite the message and self-heal the stored badge so the next render is correct.
    const url = error.config?.url || '';
    if (status === 403 && !url.includes('/auth/')) {   // skip /auth/* to avoid recursion
      try {
        const me = (await api.get('/auth/me')).data?.data;
        const rendered = JSON.parse(localStorage.getItem('user') || 'null');
        if (me?.id && rendered?.id && me.id !== rendered.id) {
          localStorage.setItem('user', JSON.stringify({ ...rendered, ...me }));
          if (error.response?.data) {
            error.response.data.error = "You've been signed in as a different user — please log in again.";
          }
        }
      } catch (_) { /* /auth/me itself failed (e.g. 401) — its own interceptor handles it */ }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) =>
  api.post('/auth/login', { email, password });

export const getMe = () =>
  api.get('/auth/me');

export const logout = () =>
  api.post('/auth/logout');

// Orders
export const getOrders = (params) =>
  api.get('/orders', { params });

export const getOrder = (id) =>
  api.get(`/orders/${id}`);

export const createOrder = (data) =>
  api.post('/orders', data);

export const updateOrderStatus = (id, status) =>
  api.patch(`/orders/${id}/status`, { status });

export const getOrdersDashboard = () =>
  api.get('/orders/dashboard/summary');

// Buyers
export const getBuyers = () =>
  api.get('/buyers');

export const getBuyer = (id) =>
  api.get(`/buyers/${id}`);

export const createBuyer = (data) =>
  api.post('/buyers', data);

export const updateBuyer = (id, data) =>
  api.patch(`/buyers/${id}`, data);

export default api;